<script lang="ts">
	/**
	 * `/Settings/Tax` — the fallback tax table (Design §10.1).
	 *
	 * **Only in force when the payment provider is not calculating tax itself.** Where the
	 * provider's own engine is on it knows every rate, threshold and reverse-charge rule and keeps
	 * knowing them as they change; this table is what a store falls back to when that is turned
	 * off, and it is deliberately a flat standard rate per country and nothing more.
	 *
	 * The seeded rates are a **snapshot, not an authority**. VAT rates move at a few weeks' notice
	 * and often mid-year, which is exactly why they are editable rows rather than constants in a
	 * deploy — and why this screen says so rather than presenting them as settled.
	 */

	import { onMount } from 'svelte';
	import {
		Badge,
		Button,
		Input,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell,
		Toggle
	} from 'flowbite-svelte';
	import { CountrySelect, EmptyState, FormField, FormModal, PageHeader, Panel, RoutedLink, SelectField } from '../../components';
	import { listTaxIDFormats, listTaxRates, upsertTaxIDFormat, upsertTaxRate } from '../../AppNetwork';
	import { countryName, taxIDPrefixName } from '../../Countries';
	import type { SelectOption, TaxCoverageResponse, TaxIDFormat, TaxMode, TaxRate } from '../../AppTypes';
	import { toastError, toastSuccess } from '../../stores/toast';

	let rates: TaxRate[] = [];
	/**
	 * Where the store can and cannot sell, which the rows alone do not say.
	 *
	 * Every rate here is charged wherever it applies. What this catches is the gap in the other
	 * direction: a country the seller is liable in with no rate on file refuses checkout, and that
	 * refusal happens one customer at a time with nothing reaching the admin.
	 */
	let coverage: TaxCoverageResponse | null = null;
	let loading: boolean = true;
	let loadError: string = '';
	let saving: boolean = false;

	/** The row being corrected, or null when adding one. */
	let editing: TaxRate | null = null;
	let isOpen: boolean = false;
	let draft = {
		country: '',
		percent: '0',
		businessPercent: '0',
		name: '',
		mode: 'standard' as TaxMode,
		invoiceNote: '',
		businessMode: 'standard' as TaxMode,
		businessInvoiceNote: '',
		allowProfessionalIndividuals: false,
		requireTaxID: false,
		isActive: true
	};

	/**
	 * The three modes, named for what they *do* rather than for the value they store.
	 *
	 * "Reverse charge" is the term on the invoice and in the law, so it stays; the other two are
	 * written as sentences because `standard` and `no_tax` say nothing on their own about what a
	 * customer is charged, and this control is the one place that decision is made.
	 */
	const MODES: SelectOption[] = [
		{
			value: 'standard',
			name: 'Charge the rate above',
			description: 'The ordinary case. The buyer is charged the rate above.'
		},
		{
			value: 'reverse_charge',
			name: 'Reverse charge for buyers with a tax ID',
			description:
				'A buyer who supplies a tax ID is charged nothing and accounts for it themselves. One who supplies none is charged the rates above — reverse charge moves the liability onto somebody registered to carry it, and a private customer is not.'
		},
		{
			value: 'no_tax',
			name: 'Charge no tax here',
			description:
				'Nothing is charged to these buyers in this country, and the invoice says why. Different from having no row at all, which also charges nothing but cannot be told from an oversight.'
		}
	];

	/** What the table column says for a mode. The dropdown's wording, shortened to fit a cell. */
	const MODE_LABELS: Record<TaxMode, string> = {
		standard: 'Charged',
		reverse_charge: 'Reverse charge',
		no_tax: 'No tax'
	};

	/**
	 * Whether the mode needs a sentence for the invoice — which is both modes that charge nothing.
	 *
	 * A zero on an invoice explains nothing: reverse-charged, exempt and never-worked-out are the
	 * same six characters, and an unexplained one is what an auditor reads as tax that was owed and
	 * missed. So the field appears with the mode and is required by it, and the server refuses the
	 * pair in both directions.
	 */
	$: noteRequired = draft.mode !== 'standard';
	/** The same question for the business side, which has its own mode and its own sentence. */
	$: businessNoteRequired = draft.businessMode !== 'standard';

	/**
	 * Percent on screen, basis points on the wire.
	 *
	 * An admin thinks in "19%", and the table stores 1900 because Finland's 25.5% cannot be held
	 * exactly as a float and a rate a hair off is a hair off on every invoice it touches. The
	 * conversion happens here, once, rather than in the reader's head.
	 */
	function toPercent(basisPoints: number): string {
		return (basisPoints / 100).toString();
	}

	function toBasisPoints(percent: string): number {
		return Math.round(Number(percent) * 100);
	}

	$: uncovered = coverage?.uncoveredEUStates ?? [];

	/**
	 * One validator for both fields, because both are the same figure with the same bounds.
	 *
	 * Written once rather than duplicated per field: two copies of a range check are two places for
	 * the bound to drift, and the one that drifts is the one nobody is looking at.
	 */
	function percentProblem(value: string): string {
		const parsed = Number(value);
		if (value.trim() === '' || !Number.isFinite(parsed)) return 'Enter a rate, such as 19 or 25.5.';
		if (parsed < 0 || parsed > 100) return 'A rate is between 0 and 100 percent.';
		return '';
	}

	$: percentError = percentProblem(draft.percent);
	$: businessPercentError = percentProblem(draft.businessPercent);
	$: canSave =
		/^[A-Za-z]{2}$/.test(draft.country) &&
		percentError === '' &&
		businessPercentError === '' &&
		(!noteRequired || draft.invoiceNote.trim().length > 0) &&
		(!businessNoteRequired || draft.businessInvoiceNote.trim().length > 0);

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			coverage = await listTaxRates();
			rates = coverage.items;
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	function fnStartAdd(): void {
		editing = null;
		draft = {
			country: '',
			percent: '0',
			businessPercent: '0',
			name: '',
			mode: 'standard',
			invoiceNote: '',
			businessMode: 'standard',
			businessInvoiceNote: '',
			allowProfessionalIndividuals: false,
			requireTaxID: false,
			isActive: true
		};
		isOpen = true;
	}

	function fnStartEdit(rate: TaxRate): void {
		editing = rate;
		draft = {
			country: rate.country,
			percent: toPercent(rate.basisPoints),
			businessPercent: toPercent(rate.businessBasisPoints),
			name: rate.name,
			mode: rate.mode,
			invoiceNote: rate.invoiceNote ?? '',
			businessMode: rate.businessMode,
			businessInvoiceNote: rate.businessInvoiceNote ?? '',
			allowProfessionalIndividuals: rate.allowProfessionalIndividuals === true,
			requireTaxID: rate.requireTaxID === true,
			isActive: rate.isActive
		};
		isOpen = true;
	}

	async function fnSave(): Promise<void> {
		saving = true;
		try {
			await upsertTaxRate(draft.country.toUpperCase(), {
				basisPoints: toBasisPoints(draft.percent),
				businessBasisPoints: toBasisPoints(draft.businessPercent),
				name: draft.name.trim(),
				mode: draft.mode,
				// Null rather than the empty string, and never sent at all for `standard`: the
				// server refuses a note on a mode that would never print it, for the same reason it
				// refuses a mode with none — text that is stored, shown and never used is the shape
				// §10.1 already had to be taught to surface once.
				invoiceNote: noteRequired ? draft.invoiceNote.trim() : null,
				// Always sent, never left to default: omitted, the server copies the consumer's mode
				// onto businesses, which is right for a client that knows one mode and wrong for this
				// one.
				businessMode: draft.businessMode,
				businessInvoiceNote: businessNoteRequired ? draft.businessInvoiceNote.trim() : null,
				allowProfessionalIndividuals: draft.allowProfessionalIndividuals,
				// Only meaningful beside a reverse-charge business mode, which the server also enforces.
				requireTaxID: draft.businessMode === 'reverse_charge' && draft.requireTaxID,
				isActive: draft.isActive
			});
			toastSuccess(`${draft.country.toUpperCase()} saved.`);
			isOpen = false;
			editing = null;
			await fnLoad();
		} catch (error) {
			toastError('The rate was not saved.', (error as Error).message);
		} finally {
			saving = false;
		}
	}

	/*
	 * The tax ID formats. Read on their own, so a failure here leaves the rates usable. Each row keeps its
	 * own draft, and a row is only saved when its Save is pressed, so an edit half-typed into one format
	 * cannot be written by pressing another row's button.
	 */
	let formats: TaxIDFormat[] = [];
	let formatsError: string = '';
	let formatDrafts: Record<string, { pattern: string; example: string }> = {};
	let savingFormatKey: string = '';
	let addingFormat: boolean = false;
	let newFormat = { country: '', taxIDType: '', pattern: '', example: '' };

	/** Identifies one format: the country a number opens with, and the type it is checked as. */
	function formatKey(country: string, taxIDType: string): string {
		return `${country}:${taxIDType}`;
	}

	/**
	 * Whether a row's draft differs from what is on file, so that its Save is worth pressing. The drafts
	 * are an argument rather than read here, so the template re-runs when one is typed into.
	 */
	function formatChanged(format: TaxIDFormat, drafts: Record<string, { pattern: string; example: string }>): boolean {
		const draft = drafts[formatKey(format.country, format.taxIDType)];
		return draft !== undefined && (draft.pattern !== format.pattern || draft.example !== format.example);
	}

	async function fnLoadFormats(): Promise<void> {
		try {
			formats = await listTaxIDFormats();
			formatsError = '';
			// Re-read from what was saved, so a draft from before a save cannot stand in for it.
			formatDrafts = Object.fromEntries(
				formats.map((format) => [
					formatKey(format.country, format.taxIDType),
					{ pattern: format.pattern, example: format.example }
				])
			);
		} catch (error) {
			formatsError = (error as Error).message;
		}
	}

	/** Saves one row's draft. The server's refusal, when there is one, is the sentence shown. */
	async function fnSaveFormat(country: string, taxIDType: string): Promise<void> {
		const key = formatKey(country, taxIDType);
		const draft = formatDrafts[key];
		if (draft === undefined) return;
		savingFormatKey = key;
		try {
			await upsertTaxIDFormat(country, taxIDType, { pattern: draft.pattern.trim(), example: draft.example.trim() });
			toastSuccess(`${country} ${taxIDType} format saved.`);
			await fnLoadFormats();
		} catch (error) {
			toastError('The format was not saved.', (error as Error).message);
		} finally {
			savingFormatKey = '';
		}
	}

	/** Adds a format for a country and type that has none yet. */
	async function fnAddFormat(): Promise<void> {
		const country = newFormat.country.trim().toUpperCase();
		const taxIDType = newFormat.taxIDType.trim();
		savingFormatKey = formatKey(country, taxIDType);
		try {
			await upsertTaxIDFormat(country, taxIDType, {
				pattern: newFormat.pattern.trim(),
				example: newFormat.example.trim()
			});
			toastSuccess(`${country} ${taxIDType} format added.`);
			newFormat = { country: '', taxIDType: '', pattern: '', example: '' };
			addingFormat = false;
			await fnLoadFormats();
		} catch (error) {
			toastError('The format was not saved.', (error as Error).message);
		} finally {
			savingFormatKey = '';
		}
	}

	onMount(() => {
		void fnLoad();
		void fnLoadFormats();
	});
</script>

<PageHeader title="Tax">
	<svelte:fragment slot="description">
		The rates used when the payment provider is not calculating tax itself.
	</svelte:fragment>
</PageHeader>

<Panel title="Rates by country" faIcon="percent">
	<svelte:fragment slot="titleRight">
		<Button size="sm" color="primary" on:click={fnStartAdd}>
			<i class="fa-solid fa-plus mr-2" />Add a country
		</Button>
	</svelte:fragment>

	<!--
		Said plainly and near the numbers rather than buried in help text. An admin reading a table
		of rates will assume it is authoritative unless told otherwise, and the consequence of that
		assumption is charging the wrong VAT for however long it takes someone to notice.
	-->
	<p class="text-sm text-default mb-4">
		<i class="fa-solid fa-circle-info mr-2" />These apply only where the payment provider is
		<strong>not</strong> calculating tax itself. They ship as a snapshot and rates change, so check
		them against the current published rates before relying on them, and correct any that have
		moved.
	</p>

	{#if coverage !== null}
		<!--
			Where the store sells from, stated on the same screen as the rates. It does not decide
			whether a rate applies — every rate here is charged — but it decides which sales are
			domestic and whether EU reverse charge is in play, and it lives in a setting most admins
			will never have touched.
		-->
		<div class="widget bg-depth mb-4 text-sm">
			<div class="flex flex-wrap items-center gap-2">
				<span class="card-label">Selling from</span>
				<Badge color="blue">{coverage.sellerCountry}</Badge>
				<span class="text-default">
					Every country with a rate below is charged that rate. A country with no rate is
					charged nothing.
					{#if coverage.sellerInEU}
						An EU buyer who supplies a valid VAT ID is reverse-charged instead, and an EU
						country with no rate on file <strong>refuses the sale</strong> rather than
						charging nothing, because {coverage.sellerCountry} is liable across the union.
					{/if}
				</span>
				<RoutedLink to="/Settings" class="text-primary-600 dark:text-primary-400 underline">
					Change under tax.sellerCountry
				</RoutedLink>
			</div>

			{#if coverage.sellerRateMissing}
				<p class="mt-3 text-amber-700 dark:text-amber-400">
					<i class="fa-solid fa-triangle-exclamation mr-2" />No active rate for
					{coverage.sellerCountry}, so domestic sales are refused rather than charged nothing.
				</p>
			{/if}

			{#if uncovered.length > 0}
				<!--
					Named state by state rather than counted. These are refused sales, not untaxed
					ones, and each one is a checkout that stops with nothing reaching the admin.
				-->
				<p class="mt-3 text-amber-700 dark:text-amber-400">
					<i class="fa-solid fa-triangle-exclamation mr-2" />No rate on file for
					<strong>{uncovered.join(', ')}</strong>. Sales into
					{uncovered.length === 1 ? 'that EU country' : 'those EU countries'} are refused
					rather than untaxed, because an EU seller is liable there under OSS.
				</p>
			{/if}
		</div>
	{/if}

	{#if loading}
		<EmptyState variant="loading" title="Loading rates…" />
	{:else if loadError.length > 0}
		<EmptyState variant="error" title="The rates could not be loaded." description={loadError}>
			<Button color="alternative" size="sm" on:click={fnLoad}>Try again</Button>
		</EmptyState>
	{:else if rates.length === 0}
		<EmptyState
			title="No rates on file."
			description="With none, a sale into a country the store is liable in is refused rather than charged nothing, which is the safe way round, but it does mean nobody can buy."
		>
			<Button color="primary" size="sm" on:click={fnStartAdd}>Add the first country</Button>
		</EmptyState>
	{:else}
		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<TableHeadCell>Country</TableHeadCell>
				<TableHeadCell>Consumer</TableHeadCell>
				<TableHeadCell>Business</TableHeadCell>
				<TableHeadCell>Shown on the invoice as</TableHeadCell>
				<TableHeadCell>State</TableHeadCell>
				<TableHeadCell />
			</TableHead>
			<TableBody>
				{#each rates as rate (`${rate.country}:${rate.region}`)}
					<TableBodyRow>
						<TableBodyCell class="dataTable font-medium">
							<!-- Named as well as coded. A column of bare pairs is a table an admin has to
							     decode to audit, and auditing it is the whole reason §10.1 shows it. -->
							{countryName(rate.country)}
							<span class="ml-2 font-mono text-xs text-default">{rate.country}</span>
						</TableBodyCell>
						<!--
							Each audience's rate with its own treatment under it, rather than one
							Treatment column: the two sides are decided separately, and a country that
							reverse-charges businesses while charging consumers looks identical to one
							charging both in every column but this.
						-->
						<TableBodyCell class="dataTable">
							<span class="tabular-nums">{toPercent(rate.basisPoints)}%</span>
							{#if rate.mode !== 'standard'}
								<div class="mt-1">
									<Badge color="purple">{MODE_LABELS[rate.mode]}</Badge>
									<!--
										The sentence itself, not merely the fact that there is one. It is
										printed on a legal document, and a screen that says "reverse charge"
										without showing the wording leaves the one thing an admin came to
										check one click away behind an edit form.
									-->
									<div class="text-xs text-default mt-1">{rate.invoiceNote ?? ''}</div>
								</div>
							{/if}
						</TableBodyCell>
						<!--
							Both rates on the row rather than one behind an edit dialog: which one a
							given sale is charged at is decided by whether the customer named a
							company, and an admin auditing the table has to be able to see the pair
							they are choosing between.
						-->
						<TableBodyCell class="dataTable">
							<span class="tabular-nums">{toPercent(rate.businessBasisPoints)}%</span>
							{#if rate.businessMode !== 'standard'}
								<div class="mt-1">
									<Badge color="purple">{MODE_LABELS[rate.businessMode]}</Badge>
									<div class="text-xs text-default mt-1">{rate.businessInvoiceNote ?? ''}</div>
								</div>
							{/if}
							{#if rate.allowProfessionalIndividuals}
								<div class="mt-1"><Badge color="indigo">Includes sole traders</Badge></div>
							{/if}
							{#if rate.requireTaxID}
								<div class="mt-1"><Badge color="red">VAT ID required</Badge></div>
							{/if}
						</TableBodyCell>
						<!-- A rate saved without a name is printed as VAT, so the table says that rather than a blank. -->
						<TableBodyCell class="dataTable">
							{#if rate.name.trim().length > 0}{rate.name}{:else}<span class="text-default">VAT</span>{/if}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							{#if rate.isActive}
								<Badge color="green">Active</Badge>
							{:else}
								<!--
									A deactivated rate refuses the sale rather than falling through to
									zero: a rate deliberately taken out of service is not the same as a
									country that was never in scope.
								-->
								<Badge color="dark">Inactive, sales refused</Badge>
							{/if}
						</TableBodyCell>
						<TableBodyCell class="dataTable">
							<Button size="xs" color="alternative" on:click={() => fnStartEdit(rate)}>
								<i class="fa-solid fa-pen mr-1" />Edit
							</Button>
						</TableBodyCell>
					</TableBodyRow>
				{/each}
			</TableBody>
		</Table>
	{/if}

	<FormModal
		bind:open={isOpen}
		title={editing === null ? 'Add a country' : `Edit ${editing.country}`}
		faIcon={editing === null ? 'plus' : 'pen'}
		submitLabel="Save rate"
		busy={saving}
		canSubmit={canSave}
		description="Applies only where the payment provider is not calculating tax itself."
		on:submit={fnSave}
	>
		<!--
			No format error under this any more: the picker cannot produce anything but a real code,
			which is the point of replacing the text field. A rate for `UK` — not a country code —
			used to be one keystroke away, and it charged nobody while looking configured.
		-->
		<FormField
			id="tax-country"
			label="Country"
			required
			description="The country this rate is charged in."
		>
			<CountrySelect
				id="tax-country"
				bind:value={draft.country}
				disabled={saving || editing !== null}
				required
			/>
		</FormField>

		<FormField
			id="tax-percent"
			label="Consumer rate"
			required
			description="Charged to a buyer who names no company. A percentage. Halves are fine, 25.5 is stored exactly."
			error={percentError}
		>
			<Input id="tax-percent" bind:value={draft.percent} placeholder="19" disabled={saving} />
		</FormField>

		<!--
			The second rate is a field rather than a rule, because the two genuinely differ by
			jurisdiction. It is chosen by the customer having given a **company name**, which is not
			the same test as reverse charge: that turns on a VAT ID, applies only to EU cross-border
			sales, and charges nothing at all rather than charging this.
		-->
		<FormField
			id="tax-business-percent"
			label="Business rate"
			required
			description="Charged when the buyer gives a company name. Often the same as the consumer rate; some jurisdictions differ."
			error={businessPercentError}
		>
			<Input
				id="tax-business-percent"
				bind:value={draft.businessPercent}
				placeholder="19"
				disabled={saving}
			/>
		</FormField>

		<FormField
			id="tax-name"
			label="Shown on the invoice as"
			description="Optional. The name of the tax on the invoice, such as “German VAT”. Defaults to VAT when left blank."
		>
			<Input id="tax-name" bind:value={draft.name} placeholder="Defaults to VAT" disabled={saving} />
		</FormField>

		<!--
			One treatment per audience, each under the rate it can override, because that is the order
			the decision is made in: an admin enters what this country charges and then says whether
			anything overrides it. Two rather than one because a jurisdiction routinely reverse-charges
			a registered business while charging a consumer its standard rate — one control for both
			was wrong for whichever side it was not set for.
		-->
		<FormField
			id="tax-mode"
			label="Consumer treatment"
			required
			description="Applied to consumers buying into this country from elsewhere. A sale in the store’s own country always charges the rates above — home is the one place the seller is unambiguously liable, and a mode honoured there would untax domestic sales."
		>
			<SelectField id="tax-mode" items={MODES} bind:value={draft.mode} disabled={saving} required />
		</FormField>

		{#if noteRequired}
			<!--
				Appears with the mode and is required by it. A mode that charges nothing leaves a
				“0.00” on a legal document, and a zero with nothing beside it is what an auditor reads
				as tax that was owed and missed — so the two are one decision and the form asks them
				together rather than letting a country be switched to zero and the sentence forgotten.
			-->
			<FormField
				id="tax-invoice-note"
				label="Printed on a consumer’s invoice"
				required
				description={draft.mode === 'reverse_charge'
					? 'Shown on every invoice reverse-charged to a consumer in this country. The wording is a legal requirement and differs by jurisdiction, so it is entered rather than assumed.'
					: 'Shown on every consumer invoice for this country, saying why no tax was charged. The wording is a legal requirement and differs by jurisdiction.'}
				error={draft.invoiceNote.trim() === '' ? 'This mode charges no tax, so the invoice has to say why.' : ''}
			>
				<Input
					id="tax-invoice-note"
					bind:value={draft.invoiceNote}
					placeholder={draft.mode === 'reverse_charge'
						? 'Reverse charge: VAT to be accounted for by the recipient'
						: 'Umsatzsteuerfrei nach §4 Nr. 1a UStG'}
					disabled={saving}
				/>
			</FormField>
		{/if}

		<FormField
			id="tax-business-mode"
			label="Business treatment"
			required
			description="Applied to buyers who give a company name, buying into this country from elsewhere. Chosen by the same test as the business rate."
		>
			<SelectField
				id="tax-business-mode"
				items={MODES}
				bind:value={draft.businessMode}
				disabled={saving}
				required
			/>
		</FormField>

		{#if businessNoteRequired}
			<FormField
				id="tax-business-invoice-note"
				label="Printed on a business’s invoice"
				required
				description={draft.businessMode === 'reverse_charge'
					? 'Shown on every invoice reverse-charged to a business in this country. The wording is a legal requirement and differs by jurisdiction, so it is entered rather than assumed.'
					: 'Shown on every business invoice for this country, saying why no tax was charged. The wording is a legal requirement and differs by jurisdiction.'}
				error={draft.businessInvoiceNote.trim() === '' ? 'This mode charges no tax, so the invoice has to say why.' : ''}
			>
				<Input
					id="tax-business-invoice-note"
					bind:value={draft.businessInvoiceNote}
					placeholder={draft.businessMode === 'reverse_charge'
						? 'Reverse charge: VAT to be accounted for by the recipient'
						: 'Umsatzsteuerfrei nach §4 Nr. 1a UStG'}
					disabled={saving}
				/>
			</FormField>
		{/if}

		<!--
			§10.1 — a sole trader or freelancer with no company. Per country, because whether an
			individual buying for their work is a business purchase is the jurisdiction's question.
		-->
		<div class="flex items-start gap-3">
			<Toggle id="tax-professionals" bind:checked={draft.allowProfessionalIndividuals} disabled={saving} />
			<label for="tax-professionals" class="text-sm">
				Individuals buying for their work count as businesses
				<span class="block text-xs text-default">
					A buyer with no company must tick "I'm buying for my work as a sole trader or freelancer" at checkout,
					and gets the business rate and mode here; a private consumer cannot buy from this country. Off, every
					individual is a consumer.
				</span>
			</label>
		</div>
		<!--
			§10.1 — registered businesses only. Only offered with a reverse-charge business mode, because the
			number is asked for in order to reverse-charge; the server refuses the combination otherwise.
		-->
		<div class="flex items-start gap-3">
			<Toggle
				id="tax-require-tax-id"
				bind:checked={draft.requireTaxID}
				disabled={saving || draft.businessMode !== 'reverse_charge'}
			/>
			<label for="tax-require-tax-id" class="text-sm">
				A VAT ID is required to buy from here
				<span class="block text-xs text-default">
					The sale is reverse-charged, so a buyer with no VAT number cannot purchase from this country.
					Needs the business mode above to be reverse charge.
				</span>
			</label>
		</div>
		<div class="flex items-center gap-3">
			<Toggle id="tax-active" bind:checked={draft.isActive} disabled={saving} />
			<label for="tax-active" class="text-sm">
				In use. Turning it off refuses sales to this country rather than charging nothing.
			</label>
		</div>
	</FormModal>
</Panel>

<Panel title="Tax ID formats" faIcon="hashtag">
	<svelte:fragment slot="titleRight">
		<Button size="sm" color="primary" on:click={() => (addingFormat = !addingFormat)}>
			<i class="fa-solid fa-plus mr-2" />Add a format
		</Button>
	</svelte:fragment>

	<p class="text-default text-sm">
		A number a customer enters for a type listed here must match the format of the country it opens with. A
		pattern is a regular expression applied to the number in upper case, with spaces and separators removed, and
		it must match the whole number. A type with no formats here is not checked by the store; the payment provider
		checks it.
	</p>

	{#if formatsError}
		<EmptyState variant="error" title="The tax ID formats could not be loaded" description={formatsError}>
			<Button color="alternative" size="sm" on:click={fnLoadFormats}>Try again</Button>
		</EmptyState>
	{/if}

	{#if addingFormat}
		<div class="grid gap-3 sm:grid-cols-2">
			<!-- The two letters a tax ID opens with, which is not always the ISO code (Greece is EL), so
			     typed rather than picked — and named as it is typed, so a wrong prefix is visible. -->
			<FormField
				id="format-country"
				label="Number prefix (two letters)"
				description={/^[A-Za-z]{2}$/.test(newFormat.country.trim()) ? taxIDPrefixName(newFormat.country) : ''}
			>
				<Input id="format-country" bind:value={newFormat.country} placeholder="DE" disabled={savingFormatKey !== ''} />
			</FormField>
			<FormField id="format-type" label="Tax ID type">
				<Input id="format-type" bind:value={newFormat.taxIDType} placeholder="eu_vat" disabled={savingFormatKey !== ''} />
			</FormField>
			<FormField id="format-pattern" label="Pattern">
				<Input id="format-pattern" class="font-mono" bind:value={newFormat.pattern} placeholder="^DE\d{9}$" disabled={savingFormatKey !== ''} />
			</FormField>
			<FormField id="format-example" label="Example">
				<Input id="format-example" bind:value={newFormat.example} placeholder="DE123456789" disabled={savingFormatKey !== ''} />
			</FormField>
		</div>
		<Button color="primary" size="sm" on:click={fnAddFormat} disabled={savingFormatKey !== ''}>Save format</Button>
	{/if}

	<Table divClass="widget !p-1 relative" class="dataTable">
		<TableHead>
			<TableHeadCell>Country</TableHeadCell>
			<TableHeadCell>Tax ID type</TableHeadCell>
			<TableHeadCell>Pattern</TableHeadCell>
			<TableHeadCell>Example</TableHeadCell>
			<TableHeadCell><span class="sr-only">Save</span></TableHeadCell>
		</TableHead>
		<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
			{#each formats as format (formatKey(format.country, format.taxIDType))}
				{@const key = formatKey(format.country, format.taxIDType)}
				<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
					<TableBodyCell>
						{taxIDPrefixName(format.country)}
						<span class="ml-2 font-mono text-xs text-default">{format.country}</span>
					</TableBodyCell>
					<TableBodyCell>{format.taxIDType}</TableBodyCell>
					<TableBodyCell>
						<Input
							class="font-mono"
							aria-label="Pattern for {format.country} {format.taxIDType}"
							bind:value={formatDrafts[key].pattern}
							disabled={savingFormatKey !== ''}
						/>
					</TableBodyCell>
					<TableBodyCell>
						<Input
							aria-label="Example for {format.country} {format.taxIDType}"
							bind:value={formatDrafts[key].example}
							disabled={savingFormatKey !== ''}
						/>
					</TableBodyCell>
					<TableBodyCell>
						<Button
							size="xs"
							color="primary"
							disabled={!formatChanged(format, formatDrafts) || savingFormatKey !== ''}
							on:click={() => fnSaveFormat(format.country, format.taxIDType)}
						>
							Save
						</Button>
					</TableBodyCell>
				</TableBodyRow>
			{/each}
		</TableBody>
	</Table>
</Panel>
